import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
} from "@mui/material";
import CustomAppBar from "../../components/AppBar";
import { useTranslation } from "react-i18next";
import api from "../../globals/axios";
import { useParams } from "react-router-dom";
import { useAlert } from "../../components/Alert";
import { isNotNullOrEmptyArray } from "../../globals/utils";
import LoaderContainer from "../../components/LoaderContainer";

const useStyles = {
  root: {
    width: "100%",
    minHeight: "100vh",
    marginBottom: "-4rem",
  },
  container: {
    padding: "20px",
    maxWidth: "1200px",
    margin: "0 auto",
  },
  title: {
    textAlign: "center",
    padding: "20px 0",
    borderRadius: "4px",
    marginBottom: "20px",
    border: "1px solid rgba(0, 0, 0, 0.12)",
  },
  detailsCard: {
    marginBottom: "20px",
    "& .MuiTypography-root": {
      marginBottom: "8px",
    },
    border: "1px solid rgba(0, 0, 0, 0.12)",
    boxShadow: "none",
  },
};

const DetailedBiocharView = () => {
  const [isLoading, setIsLoading] = useState(true);
  const { t } = useTranslation();
  const { biocharId } = useParams();
  const { showAlert } = useAlert();
  const [biochar, setBiochar] = useState(null);
  const [cropDetails, setCropDetails] = useState(null);
  const [addressDetails, setAddressDetails] = useState(null);
  const [status, setStatus] = useState("PENDING");

  useEffect(() => {
    if (biocharId) {
      fetchBiocharDetails(biocharId);
    }
  }, [biocharId]);
  useEffect(() => {
    if (biochar?.status) {
      setStatus(biochar.status);
    }
  }, [biochar]);

  const toggleStatus = async () => {
    try {
      setIsLoading(true);
      const newStatus = status === "PENDING" ? "REVIEWED" : "PENDING";
      const response = await api.patch(`/app-admin/biochar-interests/${biocharId}`, {
        status: newStatus,
      });
      if (response?.status === 200) {
        setStatus(newStatus);
        showAlert("success", t("Status updated successfully!"));
      } else {
        console.error(response);
        throw new Error("Failed to update status");
      }
    } catch (error) {
      console.error(error);
      showAlert("error", t("Couldn't update status. Please try again later!"));
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch Biochar Details
  const fetchBiocharDetails = async (id) => {
    try {
      const response = await api.get(`app-admin/biochar-interests/${id}`);
      if (response?.data) {
        let { data } = response;
        setBiochar(data);
        setAddressDetails(data.address_data);
        setCropDetails(data.crops || []);
      } else {
        showAlert(
          "error",
          t("Error fetching biochar details. Data not found!")
        );
      }
    } catch (error) {
      console.error("Error fetching biochar details:", error);
      showAlert(
        "error",
        t("Couldn't fetch biochar details. Please try again later!")
      );
    } finally {
      setIsLoading(false);
    }
  };

  const biocharFields = [
    { key: "fpo_name", label: t("FPO Name") },
    { key: "contact_person", label: t("Contact Person") },
    { key: "mobile_no", label: t("Mobile") },
    { key: "number_of_farmers", label: t("Number of Farmers") },
    { key: "total_acreage", label: t("Total Acreage"), suffix: " acres" },
    {
      key: "expected_crop_residue_kharif",
      label: t("Expected Crop Residue Kharif"),
    },
    {
      key: "expected_crop_residue_rabi",
      label: t("Expected Crop Residue Rabi"),
    },
    { key: "funding_source", label: t("Funding Source") },
  ];

  const addressKeyMapping = {
    address_line1: t("Address Line 1"),
    address_line2: t("Address Line 2"),
    district: t("District"),
    state: t("State"),
    pin_code: t("Pincode"),
    postoffice: t("Post Office"),
    village: t("Village"),
  };

  return (
    <Box sx={useStyles.root} component={Paper}>
      <CustomAppBar />
      <Box sx={useStyles.container}>
        <LoaderContainer loading={isLoading}>
          {/* Title Section */}
          <Paper sx={useStyles.title} elevation={0}>
            <Typography variant="h5">{t("Biochar Details")}</Typography>
          </Paper>
          {/* Farmer Details Section */}
          <Card sx={useStyles.detailsCard}>
            <CardContent>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: 1,
                }}
              >
                {biochar &&
                  biocharFields.map(({ key, label, suffix }) => (
                    <Typography key={key} variant="body1">
                      <strong>{label}:</strong>{" "}
                      {biochar[key]
                        ? `${biochar[key]}${suffix || ""}`
                        : t("Not Available")}
                    </Typography>
                  ))}
              </Box>
            </CardContent>
          </Card>

          {/* Crop Detail Section */}
          {isNotNullOrEmptyArray(cropDetails) && (
            <Card sx={{ mb: 2, p: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                {t("Crop Details")}
              </Typography>
              <Grid container spacing={2}>
                {cropDetails.map((crop, index) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    key={index}
                    sx={{ display: "flex" }}
                  >
                    <Card
                      variant="outlined"
                      sx={{
                        p: 2,
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        <strong>{t("Crop Id")}:</strong> {crop.id}
                      </Typography>
                      <Typography variant="body1" sx={{ flexGrow: 1 }}>
                        <strong>{t("Crop Name")}:</strong>{" "}
                        {crop.name}
                      </Typography>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Card>
          )}

          {/* Address Details Section */}

          {addressDetails ? (
            <Card sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                {t("Address Details")}
              </Typography>
              <Grid container spacing={2}>
                {Object.entries(addressKeyMapping).map(([key, label]) => (
                  <Grid item xs={12} sm={6} md={4} key={key}>
                    <Typography variant="body2">
                      <strong>{label}:</strong>{" "}
                      {addressDetails[key] || t("Not Available")}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
            </Card>
          ) : (
            <Box
              sx={{
                textAlign: "center",
                p: 2,
                backgroundColor: "#f8d7da",
                color: "#721c24",
                borderRadius: 1,
              }}
            >
              <Typography variant="body1">
                {t("No address found for this farmer")}
              </Typography>
            </Box>
          )}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={toggleStatus}
              disabled={isLoading}
            >
              {status === "PENDING"
                ? t("Mark as Reviewed")
                : t("Mark as Pending")}
            </Button>
          </Box>
        </LoaderContainer>
      </Box>
    </Box>
  );
};

export default DetailedBiocharView;
